
import Vue from 'vue'
import { Component, Prop, Watch } from 'vue-property-decorator'
import { checkSearchTerm, searchResults } from '@/utils/appstore'
import { MultiCheckboxItem, ObjectOptions } from '@/entities/survey'
import vClickOutside from 'v-click-outside'

enum OptionType {
    SELECTALL = -1,
    GROUP = -2,
}

@Component<SearchMultiCheckbox>({
    directives: {
        clickOutside: vClickOutside.directive,
    },
    components: {},
})
export default class SearchMultiCheckbox extends Vue {
    @Prop({ type: Boolean, required: false, default: false })
    public readonly disabled!: boolean
    // option related props
    @Prop({ required: true })
    public readonly allOptions!: MultiCheckboxItem[] | ObjectOptions
    @Prop({ type: Boolean, required: false, default: false })
    public readonly optionsSelectAll!: boolean
    @Prop({ type: String, required: false, default: 'No option match...' })
    public readonly optionNoneMatchPrompt!: string
    @Prop({ type: Boolean, required: false, default: false })
    public readonly optionsDropdown!: boolean
    @Prop({ type: Boolean, required: false, default: false })
    public readonly optionsHideInit!: boolean
    @Prop({ type: Boolean, required: false, default: false })
    public readonly optionSelectedGreyOut!: boolean
    @Prop({ type: Boolean, required: false, default: true })
    public readonly optionTick!: boolean
    // option related props end
    // input related props
    @Prop({ type: String, required: false, default: '' })
    public readonly input!: string
    @Prop({ type: String, required: false, default: '' })
    public readonly inputPlaceholder!: string
    @Prop({ type: Boolean, required: false, default: true })
    public readonly inputDisplay!: boolean
    @Prop({ type: String, required: false, default: '' })
    public readonly inputTitle!: string
    // input related props end

    public inputInternal = ''
    public optionNoneMatch = false
    public optionsHide = false

    public beforeMount() {
        this.optionsHide = this.optionsHideInit
    }

    public hideOptions() {
        this.optionsHideUpdate(true)
    }

    public showOptions() {
        this.optionsHideUpdate(false)
    }

    public get options() {
        let options = [] as MultiCheckboxItem[]
        const commonOpt = {
            checked: false,
            display: true,
            parentTitle: undefined,
        }
        if (!Array.isArray(this.allOptions)) {
            let opts = []
            Object.keys(this.allOptions).forEach((key) => {
                const opt = {
                    id: OptionType.GROUP,
                    title: key,
                    ...commonOpt,
                }
                // @ts-ignore
                opts = [...opts, opt, ...this.allOptions[key]]
            })
            options = opts
        } else {
            // @ts-ignore
            options = this.allOptions
        }
        if (this.optionsSelectAll) {
            options.unshift({
                id: OptionType.SELECTALL,
                title: 'Select All',
                ...commonOpt,
            })
        }
        return options
    }

    public optionClicked(option: MultiCheckboxItem) {
        if (this.disabled) {
            return
        }
        option.checked = !option.checked
        const selectAllOption = this.options.find(
            ({ id }) => id === OptionType.SELECTALL
        )

        switch (option.id) {
            case OptionType.SELECTALL: {
                this.options.forEach(
                    (childOption) => (childOption.checked = option.checked)
                )
                break
            }
            case OptionType.GROUP: {
                this.options.forEach((childOption) => {
                    if (childOption.parentTitle === option.title) {
                        childOption.checked = option.checked
                    }
                })
                if (selectAllOption && !option.checked) {
                    selectAllOption.checked = false
                }
                break
            }
            default: {
                if (option.checked) {
                    break
                }
                const parentOption = this.options.find(
                    ({ title }) => title === option.parentTitle
                )
                if (parentOption) {
                    parentOption.checked = false
                }
                if (selectAllOption) {
                    selectAllOption.checked = false
                }
            }
        }

        this.optionsSelectedIdsEmitter()
        this.$emit('option-clicked', option)
        this.hideOptions()
    }

    public optionsSelectedIdsEmitter() {
        const optionsSelectedIds = this.options
            .filter(({ checked, id }) => {
                return checked && (id || id > 0)
            })
            .map(({ id }) => id)
        this.$emit('options-selected-ids', optionsSelectedIds)
    }

    public get searchTerm() {
        // if there is an input prop, take input prop
        if (this.input) {
            return this.input
        }
        // otherwise take inputInternal
        return this.inputInternal
    }
    public set searchTerm(newVal) {
        this.optionSelectedReset(newVal)
        this.$emit('input-update', newVal)
        this.inputInternal = newVal
        checkSearchTerm(this.options, newVal)
        this.optionNoneMatch = searchResults(this.options)
    }

    private optionsHideUpdate(e: boolean) {
        if (this.optionsDropdown) {
            this.optionsHide = e
            this.$emit('options-hide-update', e)
        }
    }

    private optionSelectedReset(newVal: string) {
        if (this.optionsDropdown && !newVal) {
            this.options.forEach((opt) => {
                if (opt.title === this.searchTerm) {
                    opt.checked = false
                }
            })
        }
    }
}
